import { useCallback, useEffect, useRef } from 'react';
import { commitPendingSiteAssignments, } from '../components/consent_template_site_picker.component';
const SITE_AUTOSAVE_DEBOUNCE_MS = 800;
/**
 * Debounced save for wizard site checkboxes. Those picks are not on the document, so the document
 * autosave never sees them. Assignment is by template key, so a saved pick also reaches the
 * published version of that template. Closing the editor drops a pick that is still inside the
 * debounce window, so Cancel does not store it.
 */
export const useConsentSiteAutosave = ({ templateKey, pendingByEnv, sites, saveAssignments, isSuspended, onError, }) => {
    const timeoutRef = useRef();
    const pendingRef = useRef(null);
    const savedRef = useRef(null);
    const queueRef = useRef(Promise.resolve());
    const lastResultRef = useRef(Promise.resolve(true));
    const latestRef = useRef(pendingByEnv);
    latestRef.current = pendingByEnv;
    const sitesRef = useRef(sites);
    sitesRef.current = sites;
    const saveAssignmentsRef = useRef(saveAssignments);
    saveAssignmentsRef.current = saveAssignments;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const snapshot = JSON.stringify(pendingByEnv);
    const commit = useCallback((pending, key, pendingSnapshot) => {
        const save = saveAssignmentsRef.current;
        if (!save || Object.keys(pending).length === 0) {
            const skipped = Promise.resolve(true);
            lastResultRef.current = skipped;
            return skipped;
        }
        const previous = savedRef.current;
        // Mark the snapshot only once the promise is the one flush will wait on. A Save that lands
        // while this PUT is in flight must see the failure, not treat the snapshot as already stored.
        savedRef.current = { templateKey: key, snapshot: pendingSnapshot };
        const result = queueRef.current.then(async () => {
            const ok = await commitPendingSiteAssignments({
                saveAssignments: save,
                templateKey: key,
                pendingByEnv: pending,
                sites: sitesRef.current,
            });
            if (!ok && savedRef.current?.templateKey === key && savedRef.current.snapshot === pendingSnapshot) {
                savedRef.current = previous;
                onErrorRef.current();
            }
            return ok;
        });
        lastResultRef.current = result;
        queueRef.current = result.then(() => undefined, () => undefined);
        return result;
    }, []);
    useEffect(() => {
        if (!templateKey || !saveAssignments) {
            savedRef.current = null;
            return;
        }
        // Opening a template adopts the empty local selection. The picker has not reported a change yet.
        if (savedRef.current?.templateKey !== templateKey) {
            savedRef.current = { templateKey, snapshot };
            return;
        }
        if (isSuspended || snapshot === savedRef.current.snapshot || Object.keys(pendingByEnv).length === 0) {
            return;
        }
        pendingRef.current = pendingByEnv;
        timeoutRef.current = setTimeout(() => {
            pendingRef.current = null;
            void commit(pendingByEnv, templateKey, snapshot);
        }, SITE_AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(timeoutRef.current);
    }, [templateKey, snapshot, pendingByEnv, saveAssignments, isSuspended, commit]);
    const flush = useCallback(async () => {
        clearTimeout(timeoutRef.current);
        pendingRef.current = null;
        if (!templateKey || !saveAssignmentsRef.current) {
            await queueRef.current;
            return true;
        }
        const pending = latestRef.current;
        const nextSnapshot = JSON.stringify(pending);
        if (savedRef.current?.snapshot === nextSnapshot || Object.keys(pending).length === 0) {
            return lastResultRef.current;
        }
        return commit(pending, templateKey, nextSnapshot);
    }, [commit, templateKey]);
    return { flush };
};
